import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { CheckCircle, XCircle, ChevronRight, RotateCcw, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuizComponent = ({ hubId, onComplete }) => {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState(null);

  useEffect(() => {
    fetchQuiz();
  }, [hubId]);

  const fetchQuiz = async () => {
    try {
      const response = await axios.get(`${API}/quizzes/${hubId}`);
      setQuiz(response.data);
      setSelectedAnswers(new Array(response.data.questions.length).fill(-1));
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch quiz:', error);
      toast.error('Failed to load quiz');
      setLoading(false);
    }
  };

  const handleSelectAnswer = (answerIndex) => {
    if (showResults) return;
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestion] = answerIndex;
    setSelectedAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to submit quiz');
      return;
    }

    const unanswered = selectedAnswers.findIndex(a => a === -1);
    if (unanswered !== -1) {
      toast.error(`Please answer question ${unanswered + 1}`);
      setCurrentQuestion(unanswered);
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/quizzes/${hubId}/submit`,
        {
          hub_id: hubId,
          quiz_id: quiz.id,
          answers: selectedAnswers
        },
        { headers: getAuthHeaders() }
      );
      setResult(response.data);

      // Fetch correct answers
      const answersResponse = await axios.get(
        `${API}/quizzes/${hubId}/answers`,
        { headers: getAuthHeaders() }
      );
      setQuizAnswers(answersResponse.data);
      
      setShowResults(true);
      if (onComplete) onComplete(response.data);
      toast.success(`Quiz completed! Score: ${response.data.percentage.toFixed(0)}%`);
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      toast.error('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetake = () => {
    setCurrentQuestion(0);
    setSelectedAnswers(new Array(quiz.questions.length).fill(-1));
    setShowResults(false);
    setResult(null);
    setQuizAnswers(null);
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="space-y-2">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-12 bg-muted rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!quiz) {
    return (
      <Card className="w-full">
        <CardContent className="p-8 text-center text-muted-foreground">
          Quiz not available
        </CardContent>
      </Card>
    );
  }

  const progress = ((currentQuestion + 1) / quiz.questions.length) * 100;
  const question = quiz.questions[currentQuestion];

  return (
    <Card className="w-full" data-testid="quiz-component">
      <CardHeader>
        <CardTitle className="font-serif text-2xl">{quiz.title}</CardTitle>
        <CardDescription>
          {showResults 
            ? `Your score: ${result?.score}/${result?.total_questions} (${result?.percentage.toFixed(0)}%)`
            : `Question ${currentQuestion + 1} of ${quiz.questions.length}`
          }
        </CardDescription>
        <Progress value={showResults ? 100 : progress} className="mt-2" />
      </CardHeader>
      
      <CardContent className="space-y-6">
        {showResults ? (
          <div className="space-y-6">
            {/* Results Summary */}
            <div className="p-6 rounded-lg bg-muted/50 text-center">
              <div className={`text-4xl font-serif font-bold mb-2 ${
                result.percentage >= 80 ? 'text-green-600' : 
                result.percentage >= 60 ? 'text-yellow-600' : 'text-destructive'
              }`}>
                {result.percentage.toFixed(0)}%
              </div>
              <p className="text-muted-foreground">
                {result.percentage >= 80 
                  ? 'Excellent! You have a strong grasp of this material.'
                  : result.percentage >= 60 
                    ? 'Good effort! Review the material to strengthen your understanding.'
                    : 'Keep studying! Review the content and try again.'}
              </p>
            </div>

            {/* Review Questions */}
            <div className="space-y-4">
              <h4 className="font-semibold">Review Your Answers:</h4>
              {quizAnswers?.questions.map((q, qIndex) => (
                <div key={qIndex} className="p-4 rounded-lg border">
                  <div className="flex items-start gap-2 mb-3">
                    {selectedAnswers[qIndex] === q.correct ? (
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                    )}
                    <p className="font-medium">{q.question}</p>
                  </div>
                  <div className="space-y-2 ml-7">
                    {q.options.map((option, oIndex) => (
                      <div 
                        key={oIndex}
                        className={`p-2 rounded text-sm ${
                          oIndex === q.correct 
                            ? 'bg-green-100 text-green-800 border border-green-300'
                            : oIndex === selectedAnswers[qIndex] && oIndex !== q.correct
                              ? 'bg-red-100 text-red-800 border border-red-300'
                              : 'bg-muted/30'
                        }`}
                      >
                        {option}
                        {oIndex === q.correct && <span className="ml-2 text-xs">(Correct)</span>}
                        {oIndex === selectedAnswers[qIndex] && oIndex !== q.correct && (
                          <span className="ml-2 text-xs">(Your answer)</span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 ml-7 p-3 bg-primary/5 rounded text-sm italic border-l-2 border-primary">
                    <BookOpen className="h-4 w-4 inline mr-2 text-primary" />
                    {q.scripture}
                  </div>
                </div>
              ))}
            </div>

            <Button onClick={handleRetake} className="w-full btn-hover-lift" data-testid="quiz-retake">
              <RotateCcw className="h-4 w-4 mr-2" />
              Retake Quiz
            </Button>
          </div>
        ) : (
          <>
            {/* Current Question */}
            <div>
              <h3 className="text-lg font-medium mb-4">{question.question}</h3>
              <div className="space-y-3">
                {question.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleSelectAnswer(index)}
                    className={`quiz-option w-full p-4 text-left rounded-lg border transition-colors ${
                      selectedAnswers[currentQuestion] === index
                        ? 'selected border-primary bg-primary/10'
                        : 'border-border hover:bg-muted'
                    }`}
                    aria-pressed={selectedAnswers[currentQuestion] === index}
                    data-testid={`quiz-option-${index}`}
                  >
                    <span className="font-medium mr-2">{String.fromCharCode(65 + index)}.</span>
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Scripture Reference */}
            <div className="p-4 bg-muted/30 rounded-lg border-l-2 border-primary">
              <p className="text-sm text-muted-foreground italic">
                <BookOpen className="h-4 w-4 inline mr-2" />
                {question.scripture}
              </p>
            </div>

            {/* Navigation */}
            <div className="flex justify-between gap-4">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                data-testid="quiz-prev"
              >
                Previous
              </Button>
              
              {currentQuestion === quiz.questions.length - 1 ? (
                <Button 
                  onClick={handleSubmit} 
                  disabled={submitting || !isAuthenticated}
                  className="btn-hover-lift"
                  data-testid="quiz-submit"
                >
                  {submitting ? 'Submitting...' : 'Submit Quiz'}
                </Button>
              ) : (
                <Button 
                  onClick={handleNext}
                  className="btn-hover-lift"
                  data-testid="quiz-next"
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>

            {!isAuthenticated && (
              <p className="text-sm text-muted-foreground text-center">
                Please log in to submit your quiz and track progress.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default QuizComponent;
